import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";

import api from "../../../api/api";
import { addMessage, setThread } from "../../../Redux/new-chat-store/chatSlice";
import "../../Forms/common/chat-sending.css";

import { buildProviderChatPayload } from "./providerChat.mjs";

const getErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail || error.response?.data?.error;
  return typeof detail === "string" ? detail : error.message || fallback;
};

const ProviderChatPopup = ({ provider, handleClose }) => {
  const dispatch = useDispatch();
  const titleId = useId();
  const messageId = useId();
  const messageRef = useRef(null);
  const requestRef = useRef(null);
  const sendingRef = useRef(false);
  const messageAttemptRef = useRef(null);
  const [thread, setChatThread] = useState(null);
  const [opening, setOpening] = useState(true);
  const [openingError, setOpeningError] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [, setSendError] = useState("");
  const [sent, setSent] = useState(false);
  const providerName = provider.treatment_location?.name || "Medical provider";

  const openThread = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setOpening(true);
    setOpeningError("");
    setChatThread(null);
    try {
      const payload = buildProviderChatPayload(provider);
      const { data } = await api.post("/api/chat/case-threads/", payload, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!data?.id) throw new Error("The provider chat could not be opened. Please try again.");
      dispatch(setThread({ threadId: data.id, thread: data }));
      setChatThread(data);
    } catch (error) {
      if (!controller.signal.aborted) {
        setOpeningError(getErrorMessage(error, "Unable to open provider chat. Please try again."));
      }
    } finally {
      if (!controller.signal.aborted) setOpening(false);
    }
  }, [dispatch, provider]);

  useEffect(() => {
    openThread();
    return () => requestRef.current?.abort();
  }, [openThread]);

  const handleSend = async (event) => {
    event.preventDefault();
    const content = message.trim();
    if (!content || !thread?.id || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    setSendError("");
    try {
      // Retain the ID when retrying the same text after a lost response.
      if (messageAttemptRef.current?.content !== content) {
        messageAttemptRef.current = { content, client_message_id: crypto.randomUUID() };
      }
      const { data } = await api.post(
        `/api/chat/threads/${thread.id}/messages/`,
        messageAttemptRef.current
      );
      const sentMessage = data?.message || data;
      if (sentMessage?.id) {
        dispatch(
          addMessage({
            threadId: thread.id,
            message: { ...sentMessage, thread_id: thread.id },
          })
        );
      }
      setSent(true);
      handleClose();
    } catch (error) {
      setSendError(getErrorMessage(error, "Unable to send this message. Please try again."));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  return (
    <Modal
      show
      centered
      onHide={sending ? undefined : handleClose}
      onEntered={() => messageRef.current?.focus()}
      backdrop={sending ? "static" : true}
      keyboard={!sending}
      aria-labelledby={titleId}
      dialogClassName="custom-chat-sending-modal-width treatment-provider-chat-dialog"
      contentClassName="chat-sending-content treatment-provider-chat-content"
    >
      <form onSubmit={handleSend} className="treatment-provider-chat-form">
        <Modal.Header className="chat-sending-header height-25 p-0 bg-primary popup-heading-color justify-content-center">
          <Modal.Title as="h2" id={titleId} className="chat-sending-title popup-heading-color">
            Send Message Through Chat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="chat-sending-body">
          <div className="treatment-provider-chat-recipient">
            <span className="chat-sending-placeholder">To:</span>
            <strong>{providerName}</strong>
            {provider.specialty?.name && <span>{provider.specialty.name}</span>}
          </div>

          <>
            <label
              htmlFor={messageId}
              className="chat-sending-message-label bg-primary popup-heading-color"
            >
              Message
            </label>
            <textarea
              id={messageId}
              name="provider-chat-message"
              ref={messageRef}
              className="form-control chat-sending-input"
              rows={4}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              disabled={sending}
            />
          </>
        </Modal.Body>
        <Modal.Footer className="chat-sending-footer">
          <Button
            type="button"
            variant="secondary"
            className="chat-sending-cancel"
            onClick={handleClose}
            disabled={sending}
          >
            {"Cancel"}
          </Button>
          {!sent &&
            (openingError ? (
              <Button
                type="button"
                variant="primary"
                className="chat-sending-send"
                onClick={(event) => {
                  event.preventDefault();
                  openThread();
                }}
                disabled={opening}
              >
                Retry Opening Chat
              </Button>
            ) : (
              <Button
                type="submit"
                variant="success"
                className="chat-sending-send"
                disabled={opening || !thread?.id || !message.trim() || sending}
                aria-busy={sending}
              >
                {sending ? "Sending…" : "Send Message"}
              </Button>
            ))}
        </Modal.Footer>
      </form>
    </Modal>
  );
};

ProviderChatPopup.propTypes = {
  provider: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ProviderChatPopup;
